import { useEffect, useState } from "react";
import { api } from "../../data";
import { PLATFORM_NAME } from "../../lib/marke";

/**
 * Logo der Agentur (öffentlicher Bucket „branding“) für Dokumente und Außendarstellung der Agentur: Rechnungen, Gutschriften,
 * Mahnungen, Angebote, Verträge, Media-Kit, Freigabe- und Berichtsseiten. Verbindliche Regel seit der Marke CreatorOffice
 * (29.09.2026): Kopf und Rahmen des Portals tragen immer das CreatorOffice-Logo (Logo.tsx); ein Agenturlogo ersetzt es dort nie.
 */

export { PLATFORM_NAME };

/**
 * Titel des Browser-Tabs: Agentur und Produktname, ohne Dopplung („Studio Nordlicht · CreatorOffice“). Der Rahmen des Portals
 * gibt keinen eigenen Portalnamen mit (Regel: der Rahmen trägt immer CreatorOffice); portalName bleibt für Aufrufer außerhalb.
 */
export function portalTitle(orgName: string | null | undefined, portalName?: string | null): string {
  const brand = portalName?.trim() || PLATFORM_NAME;
  const org = orgName?.trim();
  if (!org || org.toLowerCase() === brand.toLowerCase()) return brand;
  return `${org} · ${brand}`;
}

/** Öffentliche Adresse eines Logos (Supabase); ohne Supabase null. */
export function publicLogoUrl(path: string | null | undefined, supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL as string | undefined): string | null {
  if (!path || !supabaseUrl) return null;
  return `${supabaseUrl.replace(/\/+$/, "")}/storage/v1/object/public/branding/${path.split("/").map(encodeURIComponent).join("/")}`;
}

/**
 * Adresse des Logos für ein <img>. Supabase: öffentliche Adresse. Demo: die in dieser Sitzung hochgeladene Datei
 * (nur Bilder; sonst null, dann erscheint die Wortmarke).
 */
export function useLogoSrc(path: string | null | undefined): string | null {
  const direct = api.mode === "demo" ? null : publicLogoUrl(path);
  const [demoSrc, setDemoSrc] = useState<string | null>(null);
  useEffect(() => {
    if (api.mode !== "demo" || !path) {
      setDemoSrc(null);
      return;
    }
    let alive = true;
    let url: string | null = null;
    api.download("branding", path).then((blob) => {
      if (!alive || !blob.type.startsWith("image/")) return;
      url = URL.createObjectURL(blob);
      setDemoSrc(url);
    }).catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  return direct ?? demoSrc;
}

/**
 * Titel des Browser-Tabs einer Seite: ihre Hauptüberschrift vor dem Portaltitel („Rechnungen · Studio Nordlicht ·
 * CreatorOffice“), damit Verlauf, Tabs und die Ansage beim Seitenwechsel die Seiten unterscheiden (WCAG 2.4.2,
 * SICHT-AGENTUR-GELD-15). Ohne Überschrift bleibt der Portaltitel.
 */
export function pageTitle(heading: string | null | undefined, base: string): string {
  const h = (heading ?? "").replace(/\s+/g, " ").trim();
  if (!h) return base;
  const kurz = h.length > 70 ? `${h.slice(0, 69).trimEnd()}…` : h;
  return base.toLowerCase().startsWith(kurz.toLowerCase()) ? base : `${kurz} · ${base}`;
}

/**
 * Setzt den Titel des Browser-Tabs, solange der Rahmen sichtbar ist: Hauptüberschrift der Seite (erste h1 im Inhalt,
 * auch wenn sie erst nach dem Laden erscheint) vor dem Portaltitel.
 */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const before = document.title;
    let frame = 0;
    const apply = () => {
      frame = 0;
      const next = pageTitle(document.querySelector("main h1")?.textContent, title);
      if (document.title !== next) document.title = next;
    };
    apply();
    const observer = typeof MutationObserver === "function"
      ? new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(apply); })
      : null;
    observer?.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      document.title = before;
    };
  }, [title]);
}
